import { DaySequence, DaySequenceStatus } from '../generated/gql-types';
import dayjs from 'dayjs';

/**
 * Same physical shift split across midnight shares one {@link DaySequence.startDateISO};
 * merge clock times for UI.
 */
export function mergeSequenceGroupForDisplay(
  group: DaySequence[],
): DaySequence | undefined {
  if (group.length === 0) return undefined;
  if (group.length === 1) return group[0];
  const withIn = group.filter((s) => s.clockInTime);
  if (withIn.length === 0) return group[0];
  const sortedByClockIn = [...withIn].sort(
    (a, b) => Number(a.clockInTime!) - Number(b.clockInTime!),
  );
  const mergedClockIn = sortedByClockIn[0].clockInTime!;
  /** Last local segment (overnight split) carries the true shift end, not end-of-day on row one. */
  const lastSegmentWithClockOut = [...sortedByClockIn]
    .reverse()
    .find((s) => s.clockOutTime);
  const mergedClockOut = lastSegmentWithClockOut?.clockOutTime ?? '';
  const base = sortedByClockIn[0];
  return {
    ...base,
    clockInTime: mergedClockIn,
    clockOutTime: mergedClockOut,
  } as DaySequence;
}

/**
 * Picks anchor day sequences that touch "today" (local), then merges all rows with that
 * {@link DaySequence.startDateISO} so overnight split segments show one check-in/out range.
 */
export function mergeDaySequencesForTodayStatusPanel(
  jobSequences: DaySequence[],
): DaySequence | undefined {
  if (!jobSequences.length) return undefined;

  const todayStart = dayjs().startOf('day').valueOf();
  const todayEnd = dayjs().endOf('day').valueOf();
  const todayISO = dayjs().format('YYYY-MM-DD');

  const anchorSet = new Set<string>();
  for (const s of jobSequences) {
    if (s.startDateISO === todayISO) {
      anchorSet.add(s.startDateISO);
    }
    if (!s.clockInTime) continue;
    const ci = Number(s.clockInTime);
    const co = s.clockOutTime
      ? Number(s.clockOutTime)
      : Number.MAX_SAFE_INTEGER;
    if (ci <= todayEnd && co >= todayStart) {
      anchorSet.add(s.startDateISO);
    }
  }

  const anchorList = Array.from(anchorSet);
  if (anchorList.length === 0) return undefined;

  const resolveAnchor = (): string => {
    if (anchorList.length === 1) return anchorList[0];
    for (const a of anchorList) {
      const g = jobSequences.filter((seq) => seq.startDateISO === a);
      if (g.some((seq) => seq.status === DaySequenceStatus.InProgress)) {
        return a;
      }
    }
    return anchorList.sort(
      (a, b) =>
        Math.max(
          ...jobSequences
            .filter((seq) => seq.startDateISO === b)
            .map((seq) => Number(seq.clockInTime || 0)),
        ) -
        Math.max(
          ...jobSequences
            .filter((seq) => seq.startDateISO === a)
            .map((seq) => Number(seq.clockInTime || 0)),
        ),
    )[0];
  };

  const anchor = resolveAnchor();
  const group = jobSequences.filter((s) => s.startDateISO === anchor);
  return mergeSequenceGroupForDisplay(group);
}

export function sequencesCoveringAnchorDateISO(
  jobSequences: DaySequence[],
  dateISO: string,
): DaySequence[] {
  const anchorDay = dayjs(dateISO, 'YYYY-MM-DD').startOf('day');
  return jobSequences.filter((sequence) => {
    if (sequence.startDateISO === dateISO) {
      return true;
    }
    const startMs = Number(sequence.startDate);
    if (!Number.isNaN(startMs) && dayjs(startMs).isSame(anchorDay, 'day')) {
      return true;
    }
    const clockInMs = Number(sequence.clockInTime);
    if (
      !Number.isNaN(clockInMs) &&
      clockInMs > 0 &&
      dayjs(clockInMs).isSame(anchorDay, 'day')
    ) {
      return true;
    }
    return false;
  });
}

export function mergeDaySequencesForDateISO(
  jobSequences: DaySequence[],
  dateISO: string,
): DaySequence | undefined {
  const group = sequencesCoveringAnchorDateISO(jobSequences, dateISO);
  if (group.length === 0) return undefined;
  return mergeSequenceGroupForDisplay(group);
}

export function mergeDaySequencesForLatestContext(
  jobSequences: DaySequence[],
): DaySequence | undefined {
  if (!jobSequences.length) return undefined;
  const latest = [...jobSequences].sort(
    (a, b) => Number(b.startDate) - Number(a.startDate),
  )[0];
  const group = jobSequences.filter(
    (s) => s.startDateISO === latest.startDateISO,
  );
  return mergeSequenceGroupForDisplay(group);
}

/**
 * The open worker CICO row for a job: clocked in, not clocked out.
 * Backend allows only one IN_PROGRESS sequence per worker; if more than one
 * exists, prefer the latest clock-in.
 */
export function findOpenDaySequence(
  jobSequences: DaySequence[],
): DaySequence | undefined {
  const open = jobSequences.filter(
    (sequence) =>
      sequence.status === DaySequenceStatus.InProgress &&
      !!sequence.clockInTime &&
      !sequence.clockOutTime,
  );
  if (open.length === 0) {
    return undefined;
  }
  if (open.length === 1) {
    return open[0];
  }
  return [...open].sort(
    (a, b) => Number(b.clockInTime) - Number(a.clockInTime),
  )[0];
}
